import { Component, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { NavService } from '../../layout/nav/nav.service';
import { ClaimsService } from '../claims/services/claims.service';
import { ReservationService } from '../reservations/services/reservation.service';
import { PropertyApiService } from '../properties/services/property.api.service';
import { GuestService } from '../guests/services/guest.service';

interface DashboardStat {
  label: string;
  value: number | null;
  loading: boolean;
  routerLink: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [ MatCardModule, MatProgressSpinnerModule, RouterLink, DecimalPipe ]
})
export class DashboardComponent implements OnInit {

  activeProperties: DashboardStat = { label: 'Active Properties', value: null, loading: true, routerLink: '/properties' };
  totalProperties: DashboardStat = { label: 'Total Properties', value: null, loading: true, routerLink: '/properties' };
  totalGuests: DashboardStat = { label: 'Total Guests', value: null, loading: true, routerLink: '/guests' };
  upcomingReservations: DashboardStat = { label: 'Upcoming Reservations', value: null, loading: true, routerLink: '/reservations' };
  totalReservations: DashboardStat = { label: 'Total Reservations', value: null, loading: true, routerLink: '/reservations' };
  claimsInProgress: DashboardStat = { label: 'Claims In Progress', value: null, loading: true, routerLink: '/claims' };

  constructor(
    private readonly navService: NavService,
    private readonly claimsService: ClaimsService,
    private readonly reservationService: ReservationService,
    private readonly propertyApiService: PropertyApiService,
    private readonly guestService: GuestService
  ) { }

  ngOnInit(): void {
    this.navService.openSideNav();
    this.loadClaimsInProgress();
    this.loadUpcomingReservations();
    this.loadTotalReservations();
    this.loadActiveProperties();
    this.loadTotalProperties();
    this.loadTotalGuests();
  }

  private async loadClaimsInProgress(): Promise<void> {
    const previousSearchRequest = this.claimsService.lastSearchRequest;
    try {
      const { totalRecords } = await this.claimsService.searchClaims({
        page: 1,
        pageSize: 1,
        filterParams: [{ fieldName: 'status', value: ['In Progress'], range: false }]
      });
      this.claimsInProgress.value = totalRecords;
    } catch {
      this.claimsInProgress.value = null;
    } finally {
      this.claimsService.lastSearchRequest = previousSearchRequest;
      this.claimsInProgress.loading = false;
    }
  }

  private async loadUpcomingReservations(): Promise<void> {
    const previousSearchRequest = this.reservationService.lastSearchRequest;
    try {
      const { totalRecords } = await this.reservationService.searchReservations({
        page: 1,
        pageSize: 1,
        filterParams: [{ fieldName: 'status', value: ['Upcoming'], range: false }]
      });
      this.upcomingReservations.value = totalRecords;
    } catch {
      this.upcomingReservations.value = null;
    } finally {
      this.reservationService.lastSearchRequest = previousSearchRequest;
      this.upcomingReservations.loading = false;
    }
  }

  private async loadTotalReservations(): Promise<void> {
    const previousSearchRequest = this.reservationService.lastSearchRequest;
    try {
      const { totalRecords } = await this.reservationService.searchReservations({ page: 1, pageSize: 1 });
      this.totalReservations.value = totalRecords;
    } catch {
      this.totalReservations.value = null;
    } finally {
      this.reservationService.lastSearchRequest = previousSearchRequest;
      this.totalReservations.loading = false;
    }
  }

  private async loadActiveProperties(): Promise<void> {
    try {
      const { totalRecords } = await this.propertyApiService.searchProperties({
        page: 1,
        pageSize: 1,
        filterParams: [{ fieldName: 'status', value: ['Active'], range: false }]
      }, true);
      this.activeProperties.value = totalRecords;
    } catch {
      this.activeProperties.value = null;
    } finally {
      this.activeProperties.loading = false;
    }
  }

  private async loadTotalProperties(): Promise<void> {
    try {
      const { totalRecords } = await this.propertyApiService.searchProperties({ page: 1, pageSize: 1 }, true);
      this.totalProperties.value = totalRecords;
    } catch {
      this.totalProperties.value = null;
    } finally {
      this.totalProperties.loading = false;
    }
  }

  private async loadTotalGuests(): Promise<void> {
    const previousSearchRequest = this.guestService.lastSearchRequest;
    try {
      const { totalRecords } = await this.guestService.searchGuests({ page: 1, pageSize: 1 });
      this.totalGuests.value = totalRecords;
    } catch {
      this.totalGuests.value = null;
    } finally {
      this.guestService.lastSearchRequest = previousSearchRequest;
      this.totalGuests.loading = false;
    }
  }
}